import { useState, useEffect, useCallback } from 'react';
import SwiperType from 'swiper';

interface UseSwipeNavigationProps {
  swiperRef?: SwiperType;
  totalSlides?: number;
}

interface SwipeNavigationState {
  showPrevArrow: boolean;
  showNextArrow: boolean;
}

const SLIDE_MARGIN_RIGHT = 16;
const TRANSLATE_THRESHOLD = 5;

export const useSwipeNavigation = ({ swiperRef, totalSlides }: UseSwipeNavigationProps): SwipeNavigationState => {
  const [showPrevArrow, setShowPrevArrow] = useState(false);
  const [showNextArrow, setShowNextArrow] = useState(true);

  const calculateArrowVisibility = useCallback(() => {
    if (!swiperRef) return;

    const swiperWidth = swiperRef.width;
    const slides = swiperRef.slides;

    if (!slides || slides.length === 0) {
      setShowPrevArrow(false);
      setShowNextArrow(false);
      return;
    }

    const slidesArray = Array.isArray(slides) ? (slides as HTMLElement[]) : (Array.from(slides || []) as HTMLElement[]);

    const totalContentWidth: number = slidesArray.reduce((total: number, slide: HTMLElement, index: number) => {
      const slideWidth = slide.offsetWidth || 0;
      const marginRight = index < slidesArray.length - 1 ? SLIDE_MARGIN_RIGHT : 0;
      return total + slideWidth + marginRight;
    }, 0);

    // Check if content overflows and current position
    const hasOverflow = totalContentWidth > swiperWidth;
    const translateX = swiperRef.translate || 0;
    const maxTranslate = totalContentWidth - swiperWidth;

    const shouldShowPrev = hasOverflow && Math.abs(translateX) > TRANSLATE_THRESHOLD;
    const shouldShowNext = hasOverflow && Math.abs(translateX) < maxTranslate - TRANSLATE_THRESHOLD;

    setShowPrevArrow(shouldShowPrev);
    setShowNextArrow(shouldShowNext);
  }, [swiperRef]);

  const handleResize = useCallback(() => {
    if (!swiperRef) return;

    swiperRef.update();
    swiperRef.updateSlides();
    swiperRef.updateProgress();
    swiperRef.updateSlidesClasses();
    calculateArrowVisibility();
  }, [swiperRef, calculateArrowVisibility]);

  useEffect(() => {
    if (!swiperRef || !swiperRef.slides) {
      setShowPrevArrow(false);
      setShowNextArrow(true);
      return;
    }

    calculateArrowVisibility();

    // Event handlers
    const handleSlideChange = () => calculateArrowVisibility();
    const handleReachEnd = () => setShowNextArrow(false);
    const handleReachBeginning = () => setShowPrevArrow(false);
    const handleFromEdge = () => calculateArrowVisibility();

    // Add all event listeners
    window.addEventListener('resize', handleResize);
    swiperRef.on('slideChange', handleSlideChange);
    swiperRef.on('reachEnd', handleReachEnd);
    swiperRef.on('reachBeginning', handleReachBeginning);
    swiperRef.on('fromEdge', handleFromEdge);
    swiperRef.on('init', calculateArrowVisibility);
    swiperRef.on('resize', calculateArrowVisibility);
    swiperRef.on('orientationchange', calculateArrowVisibility);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (swiperRef) {
        swiperRef.off('slideChange', handleSlideChange);
        swiperRef.off('reachEnd', handleReachEnd);
        swiperRef.off('reachBeginning', handleReachBeginning);
        swiperRef.off('fromEdge', handleFromEdge);
        swiperRef.off('init', calculateArrowVisibility);
        swiperRef.off('resize', calculateArrowVisibility);
        swiperRef.off('orientationchange', calculateArrowVisibility);
      }
    };
  }, [swiperRef, totalSlides, calculateArrowVisibility, handleResize]);

  return { showPrevArrow, showNextArrow };
};
